import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trophy, Share2, ArrowLeft, PlayCircle, Tv } from "lucide-react";
import useSeo from "@/components/useSeo";

const fmtClock = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export default function LiveMatch() {
  const { id, mid } = useParams();
  const [sp] = useSearchParams();
  const tv = sp.get("tv") === "1";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [now, setNow] = useState(Date.now());
  const timer = useRef();

  const fetchIt = () => api.get(`/interclub/public/tournament/${id}/match/${mid}`)
    .then((r) => setData(r.data)).catch((e) => setErr(apiErr(e)));

  useEffect(() => { fetchIt(); /* eslint-disable-next-line */ }, [id, mid]);
  useEffect(() => {
    const live = data?.match?.status === "live";
    if (live) {
      timer.current = setInterval(() => { setNow(Date.now()); }, 1000);
      const poll = setInterval(fetchIt, 10000);
      return () => { clearInterval(timer.current); clearInterval(poll); };
    }
    // eslint-disable-next-line
  }, [data?.match?.status]);
  useSeo({
    title: data?.match?.team_a_name
      ? `${data.match.team_a_name} vs ${data.match.team_b_name}${data.match.status === "live" ? " — LIVE" : ""} · SphereHyx`
      : "Live Match — SphereHyx",
    description: data?.match?.team_a_name
      ? `Follow ${data.match.team_a_name} vs ${data.match.team_b_name} live${data?.tournament?.name ? ` in ${data.tournament.name}` : ""} — real-time score and match events on SphereHyx.`
      : "Follow the match live on SphereHyx.",
    canonical: `https://spherehyx.com/tournament/${id}/match/${mid}`,
  });

  if (err) return <div className="min-h-screen flex items-center justify-center bg-background grain px-4"><div className="text-center"><h1 className="font-heading font-bold text-2xl">Match not found</h1><Link to={`/tournament/${id}`}><Button className="mt-6" variant="outline">Back to tournament</Button></Link></div></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const { tournament: t, host, match: m } = data;
  const color = host?.primary_color || "#007AFF";
  const isLive = m.status === "live";
  const isDone = m.status === "completed";
  let clock = "";
  if (isLive && m.started_at) {
    const secs = Math.max(0, Math.floor((now - new Date(m.started_at).getTime()) / 1000));
    clock = fmtClock(secs);
  }
  const share = () => {
    const url = `${window.location.origin}/tournament/${id}/match/${mid}`;
    if (navigator.share) navigator.share({ title: `${m.team_a_name} vs ${m.team_b_name}`, url }).catch(() => {});
    else { try { navigator.clipboard.writeText(url); toast.success("Live link copied"); } catch { toast.error("Couldn't copy link — copy it manually"); } }
  };
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`${window.location.origin}/tournament/${id}/match/${mid}`)}`;
  const side = (tid) => (tid === m.team_a_id ? "a" : "b");
  const Badge = ({ name }) => <div className="h-24 w-24 sm:h-40 sm:w-40 rounded-3xl bg-white/15 flex items-center justify-center font-heading text-5xl text-white/80">{(name || "?")[0]}</div>;

  if (tv) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-white px-8" style={{ background: `linear-gradient(160deg, ${color}, ${host?.secondary_color || color})` }} data-testid="tv-scoreboard">
        <p className="uppercase tracking-[0.3em] text-white/70 text-sm sm:text-base mb-2">{t?.name}</p>
        <p className="text-white/60 text-xs mb-10">{m.round_name || (m.group ? `Group ${m.group}` : "Match")}</p>
        <div className="flex items-center justify-center gap-8 sm:gap-16 w-full max-w-6xl">
          <div className="flex-1 flex flex-col items-center gap-4">
            {m.team_a_logo ? <img src={m.team_a_logo} alt="" className="h-24 w-24 sm:h-40 sm:w-40 rounded-3xl object-cover border-4 border-white/25" /> : <Badge name={m.team_a_name} />}
            <p className="font-heading font-bold text-2xl sm:text-4xl text-center">{m.team_a_name}</p>
          </div>
          <div className="text-center shrink-0">
            <p className="font-heading font-black tabular-nums leading-none text-7xl sm:text-[10rem]" data-testid="tv-score">{m.score_a ?? 0}<span className="text-white/40 mx-2 sm:mx-6">:</span>{m.score_b ?? 0}</p>
            {clock && <p className="font-mono text-2xl sm:text-4xl mt-4 text-white/80" data-testid="tv-clock">{clock}</p>}
            {isLive ? <span className="inline-block mt-4 bg-red-600 px-4 py-1.5 rounded-full text-sm sm:text-lg font-bold animate-pulse">● LIVE</span> : isDone ? <p className="mt-4 text-white/70 text-lg">FULL TIME</p> : <p className="mt-4 text-white/70 text-lg">Starting soon</p>}
          </div>
          <div className="flex-1 flex flex-col items-center gap-4">
            {m.team_b_logo ? <img src={m.team_b_logo} alt="" className="h-24 w-24 sm:h-40 sm:w-40 rounded-3xl object-cover border-4 border-white/25" /> : <Badge name={m.team_b_name} />}
            <p className="font-heading font-bold text-2xl sm:text-4xl text-center">{m.team_b_name}</p>
          </div>
        </div>
        <Link to={`/tournament/${id}/match/${mid}`} className="mt-14 text-white/50 text-xs hover:text-white/80" data-testid="tv-exit">← Exit TV mode</Link>
      </div>
    );
  }

  const TeamCol = ({ name, logo }) => (
    <div className="flex-1 flex flex-col items-center gap-3">
      {logo ? <img src={logo} alt="" className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl object-cover border-2 border-white/25" /> : <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-white/15 flex items-center justify-center text-white/80 font-heading text-2xl">{(name || "?")[0]}</div>}
      <p className="text-white font-heading font-bold text-center text-sm sm:text-lg leading-tight">{name}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-background grain">
      <div className="px-6 pt-6 pb-8" style={{ background: `linear-gradient(160deg, ${color}, ${host?.secondary_color || color})` }}>
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Link to={`/tournament/${id}`} className="text-white/80 text-sm inline-flex items-center gap-1 hover:text-white"><ArrowLeft size={16} /> {t?.name}</Link>
            {isLive && <span className="inline-flex items-center gap-1.5 bg-red-600 text-white text-xs font-bold px-2.5 py-1 rounded-full animate-pulse" data-testid="live-badge">● LIVE</span>}
            {isDone && <span className="bg-white/20 text-white text-xs font-semibold px-2.5 py-1 rounded-full" data-testid="final-badge">FULL TIME</span>}
          </div>
          <p className="text-center text-white/70 text-xs uppercase tracking-wide mb-3">{m.round_name || (m.group ? `Group ${m.group}` : "Match")}{m.kickoff ? ` · ${m.kickoff.replace("T", " ")}` : ""}</p>
          <div className="flex items-center gap-3" data-testid="live-scoreboard">
            <TeamCol name={m.team_a_name} logo={m.team_a_logo} />
            <div className="text-center px-2">
              <p className="text-white font-heading font-black text-4xl sm:text-6xl tabular-nums" data-testid="live-score">{m.score_a ?? 0}<span className="mx-2 text-white/50">-</span>{m.score_b ?? 0}</p>
              {clock && <p className="text-white/80 font-mono text-sm mt-1" data-testid="live-clock">{clock}</p>}
              {isDone && <p className="text-white/70 text-xs mt-1">Final</p>}
            </div>
            <TeamCol name={m.team_b_name} logo={m.team_b_logo} />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <Button onClick={share} variant="outline" className="flex-1" data-testid="share-live-btn"><Share2 size={16} className="mr-2" /> Share live game</Button>
          <Link to={`/tournament/${id}/match/${mid}?tv=1`}><Button variant="outline" data-testid="tv-mode-btn"><Tv size={16} className="mr-2" /> TV mode</Button></Link>
          <img src={qr} alt="QR" className="h-16 w-16 rounded-lg border border-border bg-white p-1" data-testid="live-qr" />
        </div>
        {m.stream_url && (
          <a href={m.stream_url} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full rounded-md brand-gradient text-white py-3 font-semibold" data-testid="watch-stream-btn">
            <PlayCircle size={18} /> Watch live stream
          </a>
        )}

        <div>
          <h2 className="font-heading font-bold text-lg mb-3">Match events</h2>
          {(!m.events || m.events.length === 0) ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="events-empty">No events yet.</p>
          ) : (
            <div className="space-y-2" data-testid="events-timeline">
              {[...m.events].sort((a, b) => (b.minute || 0) - (a.minute || 0)).map((ev, i) => (
                <div key={ev.id || i} className={`flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 text-sm ${side(ev.team_id) === "b" ? "flex-row-reverse text-right" : ""}`} data-testid={`event-${i}`}>
                  <span className="font-mono text-xs text-muted-foreground w-8 shrink-0">{ev.minute != null ? `${ev.minute}'` : ""}</span>
                  <span className="font-semibold">{ev.label || ev.type}</span>
                  {ev.player_name && <span className="text-muted-foreground">· {ev.player_name}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-dashed border-border p-5 text-center">
          <Trophy size={18} className="mx-auto text-primary mb-1" />
          <p className="text-sm font-medium">Powered by SphereHyx</p>
          <Link to={`/tournament/${id}`}><Button variant="outline" size="sm" className="mt-3" data-testid="back-to-tournament">View full tournament</Button></Link>
        </div>
      </div>
    </div>
  );
}
